import { CircadianPhase, EnergyFeedbackType, EMADatePhaseScoreSet } from '@demind-inc/core';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import {
  formatToHHmm,
  useAuthContext,
  useUpdatePhaseEma,
  useCircadianContext,
  selectedDateAtom,
} from '../data-access';
import { useGeneralSettings } from './useGeneralSettings';

interface UpdatePhaseEmaScoreParams {
  selectedZone: CircadianPhase;
  feedbackType: EnergyFeedbackType;
  endTime: string;
  date: string;
}

export const useUpdatePhaseEmaScore = () => {
  const { user } = useAuthContext();
  const { updatePhaseEma } = useUpdatePhaseEma();
  const { phaseEmasByDate, circadianPhaseBoundariesByDate } = useCircadianContext();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();

  const getSelectedZoneEma = (selectedZone: CircadianPhase) => {
    return useMemo(() => {
      const emaScores = phaseEmasByDate.find(
        (d) => d.date === selectedDate.format('YYYY-MM-DD')
      )?.scores;
      if (!emaScores) {
        return;
      }
      return emaScores[selectedZone as keyof EMADatePhaseScoreSet];
    }, [selectedZone, phaseEmasByDate, selectedDate]);
  };

  const isFuturePhase = (phase: CircadianPhase) => {
    return useMemo(() => {
      const phaseBoundaries = circadianPhaseBoundariesByDate.find(
        ({ date }) => date === selectedDate.format('YYYY-MM-DD')
      )?.details;

      if (!phaseBoundaries) {
        return false;
      }

      const phaseStartTime = phaseBoundaries[phase]?.start;
      if (!phaseStartTime) {
        return false;
      }

      return dayjs().isBefore(dayjs(phaseStartTime));
    }, [circadianPhaseBoundariesByDate, selectedDate, phase]);
  };

  const updatePhaseEmaScore = async ({
    selectedZone,
    feedbackType,
    endTime,
    date,
  }: UpdatePhaseEmaScoreParams) => {
    if (!selectedZone) {
      return;
    }

    const targetTime = timeFormat === '12h' ? formatToHHmm(`${date} ${endTime}`) : endTime;

    const dateTime = `${date} ${targetTime}`;

    await updatePhaseEma({
      userId: user.userId,
      date,
      timezone: dayjs.tz.guess(),
      emaDatePhaseScore: {
        [selectedZone]: {
          energy: feedbackType,
          timestamp: dayjs(dateTime).format('YYYY-MM-DD HH:mm:ss'),
          timezone: dayjs.tz.guess(),
        },
      },
    });
  };

  return { updatePhaseEmaScore, getSelectedZoneEma, isFuturePhase };
};
